import { computed, ref } from "@vue/composition-api";
import { PageableSearchResult, SearchResult, SortableSearch } from "@scrile/api-provider/dist/api/types";

interface PageableState<T> extends PageableSearchResult, SearchResult<T>, SortableSearch {}

export default function usePager<T, D>(
  loader: (page: number, requestData?: D) => Promise<PageableState<T>>,
  onGetData?: (response: T[]) => void
) {
  const enableScroll = ref(false);
  const data = ref<PageableState<T>>();

  const items = computed(() => {
    return data.value?.result ?? [];
  });

  const getData = async (page: number = 0, requestData?: D, replace?: boolean) => {
    try {
      enableScroll.value = false;
      const response = await loader(page, requestData);
      data.value = {
        ...response,
        result: !data.value || replace ? response.result : data.value.result.concat(response.result),
      };
      onGetData && onGetData(data.value?.result ?? []);
    } finally {
      enableScroll.value = data.value?.hasNextPage ?? false;
    }
  };

  const getNextPage = async (cb?: Function, requestData?: D) => {
    if (!enableScroll.value) {
      return;
    }
    const nextPage = (data.value?.page ?? 0) + 1;
    await getData(nextPage, requestData, false);
    cb && cb();
  };

  const getFirstPage = (requestData?: D) => {
    return getData(0, requestData, true);
  };

  return {
    enableScroll,
    data,
    items,
    getFirstPage,
    getNextPage,
  };
}
